import { Box, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from "@mui/material"
import {
  BlockOutlined,
  EditOutlined,
  MoreVertOutlined,
  TaskAltOutlined,
  VisibilityOutlined,
} from "@mui-symbols-material/w300"
import type { CellContext } from "@tanstack/react-table"
import type { FC } from "react"
import { useRef, useState } from "react"

import { findReconcilableAs400SyncKind, isAs400SyncInProgress } from "../utils/invoiceAs400Sync"

import { PermissionActionType } from "@/constants/permissions"
import { type Invoice, type InvoiceAs400SyncKind, InvoiceStatus } from "@/graphql/codegen/graphql"
import { usePermissionManager } from "@/hooks/usePermissionManager"
import { isNil } from "@/utils/utils"

interface InvoiceRowActionsProps {
  row: CellContext<Invoice, unknown>["row"]
  onEdit?: (row: Invoice) => void
  onView?: (row: Invoice) => void
  onCancel?: (row: Invoice) => void
  onVoid?: (row: Invoice) => void
  onConfirmAs400Reconciliation?: (row: Invoice, syncKind: InvoiceAs400SyncKind) => void
}

export const InvoiceRowActions: FC<InvoiceRowActionsProps> = ({
  row,
  onEdit,
  onView,
  onCancel,
  onVoid,
  onConfirmAs400Reconciliation,
}) => {
  const anchorElRef = useRef<HTMLButtonElement>(null)
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  const { hasPermissions } = usePermissionManager()
  const canMaintain = hasPermissions([PermissionActionType.ACCOUNTS_PAYABLE_MAINTENANCE])

  const status = row.original.status

  const canEdit =
    canMaintain &&
    (status === InvoiceStatus.PendingApproval ||
      status === InvoiceStatus.Approved ||
      status === InvoiceStatus.NotApproved)
  const canCancel = canEdit
  const canView = status === InvoiceStatus.Paid
  const canVoid = canMaintain && status === InvoiceStatus.Paid
  const reconcilableAs400SyncKind = findReconcilableAs400SyncKind(row.original)
  const canConfirmAs400Reconciliation = canMaintain && reconcilableAs400SyncKind !== undefined
  // AK-5232 AC2, AK-5383 AC2, AK-5648 AC2, AK-5649 AC2/AC4: modifying actions wait while AS400 is syncing.
  const isSyncInProgress = isAs400SyncInProgress(row.original)

  // Suppress the kebab entirely when no action applies —
  // an empty menu would be a silent UI per `react-patterns.md` "Placeholder Handlers".
  const hasAnyAction = canEdit || canCancel || canView || canVoid || canConfirmAs400Reconciliation
  if (!hasAnyAction) return null

  // AK-6943 AC #1, #2: an invoice in a Pending, Approved or Processing Pay File cannot be edited or cancelled (the
  // backend rejects it), so the whole kebab is disabled with the reason. The Tooltip sits on a wrapper because a
  // disabled button does not receive the pointer events it listens for.
  if (!isNil(row.original.lockingPayFile)) {
    return (
      <Box className='flex items-center justify-end'>
        <Tooltip title='Restricted: This invoice is included in a Pay File. Remove it from the Pay File to edit or cancel.'>
          <span data-testid={`invoices.row-actions.locked-tooltip-${row.original.invoiceId}`}>
            <IconButton
              size='small'
              disabled
              data-testid={`invoices.row-actions.menu-button-${row.original.invoiceId}`}
            >
              <MoreVertOutlined />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    )
  }

  const handleMenuOpen = () => setIsMenuOpen(true)
  const handleMenuClose = () => setIsMenuOpen(false)

  const handleEdit = () => {
    handleMenuClose()
    onEdit?.(row.original)
  }

  const handleView = () => {
    handleMenuClose()
    onView?.(row.original)
  }

  const handleCancel = () => {
    handleMenuClose()
    onCancel?.(row.original)
  }

  const handleVoid = () => {
    handleMenuClose()
    onVoid?.(row.original)
  }

  const handleConfirmAs400Reconciliation = () => {
    handleMenuClose()
    if (reconcilableAs400SyncKind) onConfirmAs400Reconciliation?.(row.original, reconcilableAs400SyncKind)
  }

  return (
    <Box className='flex items-center justify-end'>
      <IconButton
        ref={anchorElRef}
        size='small'
        onClick={handleMenuOpen}
        data-testid={`invoices.row-actions.menu-button-${row.original.invoiceId}`}
      >
        <MoreVertOutlined />
      </IconButton>
      <Menu
        anchorEl={anchorElRef.current}
        open={isMenuOpen}
        onClose={handleMenuClose}
        data-testid={`invoices.row-actions.menu-${row.original.invoiceId}`}
      >
        {canEdit && (
          <MenuItem
            onClick={handleEdit}
            disabled={isSyncInProgress}
            data-testid={`invoices.row-actions.edit-menu-item-${row.original.invoiceId}`}
          >
            <ListItemIcon>
              <EditOutlined />
            </ListItemIcon>
            <ListItemText primary='Edit' />
          </MenuItem>
        )}
        {canView && (
          <MenuItem onClick={handleView} data-testid={`invoices.row-actions.view-menu-item-${row.original.invoiceId}`}>
            <ListItemIcon>
              <VisibilityOutlined />
            </ListItemIcon>
            <ListItemText primary='View' />
          </MenuItem>
        )}
        {canVoid && (
          <MenuItem
            onClick={handleVoid}
            disabled={isSyncInProgress}
            data-testid={`invoices.row-actions.void-menu-item-${row.original.invoiceId}`}
          >
            <ListItemIcon>
              <BlockOutlined />
            </ListItemIcon>
            <ListItemText primary='Void' />
          </MenuItem>
        )}
        {canCancel && (
          <MenuItem
            onClick={handleCancel}
            disabled={isSyncInProgress}
            data-testid={`invoices.row-actions.cancel-menu-item-${row.original.invoiceId}`}
          >
            <ListItemIcon>
              <BlockOutlined />
            </ListItemIcon>
            <ListItemText primary='Cancel' />
          </MenuItem>
        )}
        {canConfirmAs400Reconciliation && (
          <MenuItem
            onClick={handleConfirmAs400Reconciliation}
            data-testid={`invoices.row-actions.confirm-as400-reconciliation-menu-item-${row.original.invoiceId}`}
          >
            <ListItemIcon>
              <TaskAltOutlined />
            </ListItemIcon>
            <ListItemText primary='Confirm Manual AS400 Reconciliation' />
          </MenuItem>
        )}
      </Menu>
    </Box>
  )
}
